import { useEffect, useRef } from 'react'
import { motion } from 'motion/react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { useGame } from '@/contexts/GameProvider'
import { ENDLESS_INDEX_BASE } from '@/game/levels'
import { endlessDifficulty } from '@/game/engine/generator'
import { useLeaderboard } from '@/arcade'
import { useGameAudio } from '@/contexts/GameAudioProvider'
import { DRIFT_ART } from '@/game/art'

function Stat({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/30 px-4 py-3 flex-1">
      <PixelIcon name={icon} size={20} />
      <span className="font-display font-black text-lg tabular-nums">{value}</span>
      <span className="text-xs text-[var(--text-faint)]">{label}</span>
    </div>
  )
}

export function EndlessScreen() {
  const { startLevel } = useNavigation()
  const { save } = useProgress()
  const { endlessRun, beginEndlessRun, clearEndlessRun } = useGame()
  const board = useLeaderboard(10)
  const audio = useGameAudio()
  const submittedRef = useRef(false)

  const best = save.progress.endlessBest
  const showSummary = endlessRun.finished && endlessRun.streak > 0

  useEffect(() => {
    if (showSummary && !submittedRef.current && endlessRun.score > 0) {
      submittedRef.current = true
      void board.submit(endlessRun.score, {
        mode: 'endless',
        streak: endlessRun.streak,
        bestStreak: Math.max(best.streak, endlessRun.streak),
      })
    }
  }, [showSummary, endlessRun.score, endlessRun.streak, best.streak, board])

  const start = () => {
    audio.play('confirm')
    submittedRef.current = false
    clearEndlessRun()
    beginEndlessRun()
    startLevel(ENDLESS_INDEX_BASE)
  }

  const nextDifficulty = endlessDifficulty(best.streak)

  return (
    <ScreenShell title="Endless Drift">
      <div className="flex flex-col gap-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-[var(--r-xl)] border border-[var(--edge)]/40 shadow-raised"
        >
          <div className="relative h-44 w-full overflow-hidden">
            <img
              src={DRIFT_ART}
              alt="The Drift — endless void"
              loading="lazy"
              decoding="async"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(8,6,14,0.15) 0%, rgba(8,6,14,0.45) 60%, var(--surface) 100%)' }}
            />
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-2.5 p-4">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-black/40 backdrop-blur-sm text-[var(--ember)]">
                <PixelIcon name="infinity" size={22} />
              </span>
              <div>
                <h2 className="font-display text-lg font-black leading-none" style={{ color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
                  The maze never ends
                </h2>
                <p className="mt-1 text-xs font-medium text-white/75">One run, one streak. How far can you drift?</p>
              </div>
            </div>
          </div>
        </motion.div>

        {showSummary && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-[var(--r-lg)] bg-[var(--surface-raised)] shadow-emboss border border-[var(--ember)]/40 p-4 text-center"
          >
            <h3 className="font-display font-bold text-[var(--ember)]">Run Over</h3>
            <div className="mt-2 flex gap-2">
              <Stat icon="fire" label="Streak" value={String(endlessRun.streak)} />
              <Stat icon="score" label="Score" value={String(endlessRun.score)} />
              <Stat icon="coin" label="Coins banked" value={String(endlessRun.coins)} />
            </div>
            <p className="mt-2 text-xs text-[var(--text-faint)]">Score submitted to the Grace board.</p>
          </motion.div>
        )}

        <div className="flex gap-2">
          <Stat icon="fire" label="Best streak" value={String(best.streak)} />
          <Stat icon="trophy" label="Best score" value={String(best.score)} />
          <Stat icon="lightning" label="Next difficulty" value={`${nextDifficulty}/6`} />
        </div>

        <GameButton size="lg" icon="play" fullWidth silent onClick={start}>
          {showSummary ? 'Run It Back' : 'Start Endless Run'}
        </GameButton>
      </div>
    </ScreenShell>
  )
}
